import './AgendaFilter.scss'

import {
	Controller,
	FormProvider,
	useForm,
	useFormContext
} from 'react-hook-form'
import {Dropdown, OptionType} from '../../ui/Dropdown'
import {Error, Errors} from '../../layout/form/Errors'
import {FormFieldType, Label, switchComponent} from '../../layout/Form'
import {Prediction, getPlaces} from '../../../util/queryplaces'
import React, {useEffect, useState} from 'react'
import {useLanguage, useTranslation} from '../../../util/i18n'

import {AgendaDetailType} from '../AgendaDetail'
import AnimateHeight from 'react-animate-height'
import {DatePicker} from '../../ui/DatePicker'
import {Icon} from '../../ui/Icon'
import {Theme} from '../../Theme'
import {fetcher} from '../../../util/fetcher'
import {style} from '../../../util/style'
import {useMediaQuery} from '@material-ui/core'

const styles = style('agendafilter', ['top', 'date', 'field', 'bottom'])

export const AgendaFilter: React.FC<{
	project_key: string
	onFilterChange?: (items: Array<AgendaDetailType>) => void
}> = ({project_key, onFilterChange}) => {
	const [isLoading, setLoading] = useState(false)
	const methods = useForm({
		defaultValues: {
			city: '',
			keyword: '',
			date_start: '',
			date_end: '',
			date_type: 'on',
			location: null,
			radius: null
		}
	})
	const t = useTranslation('agenda').filters
	const {handleSubmit} = methods
	const [open, setOpen] = useState(false)
	const [error, setError] = useState(null)
	const isMobile = useMediaQuery('(max-width:1024px)')
	const lang = useLanguage()

	const loadDefaultAgenda = () => loadAgenda(null, null)

	useEffect(() => loadDefaultAgenda(), [])

	const loadAgenda = (data, _) => {
		setError(null)
		setLoading(true)
		const completeData = {
			lang,
			project: project_key,
			filter: data
		}
		fetcher('/api/filteragendaitems', {
			method: 'POST',
			body: JSON.stringify(completeData)
		})
			.then((res: ResponseType & {items: Array<AgendaDetailType>}) => {
				setLoading(false)
				onFilterChange(res.items)
			})
			.catch((err) => {
				setLoading(false)
				onFilterChange(err?.items)
				if (err?.error) setError(err?.error)
			})
	}

	return (
		<div className={styles()}>
			<Theme.Container>
				<FormProvider {...methods}>
					<a
						onClick={() => setOpen(!open)}
						className={styles('mobile').is({open})}
					>
						<Theme.H3 className={styles('mobile-title')}>{t.title}</Theme.H3>
						<div className={styles('mobile-icon')}>
							<Icon icon="chevronDown" />
						</div>
					</a>
					<AnimateHeight height={isMobile && !open ? 0 : 'auto'}>
						<form
							onSubmit={handleSubmit(loadAgenda)}
							className={styles('form')}
						>
							<AgendaFilterTop />
							<AgendaFilterBottom
								isLoading={isLoading}
								submitForm={() => loadDefaultAgenda()}
							/>
						</form>
					</AnimateHeight>
				</FormProvider>
				{error && (
					<div className={styles('errors')}>
						<Error>{t.errors[error]}</Error>
					</div>
				)}
			</Theme.Container>
		</div>
	)
}

const AgendaFilterField: React.FC<
	FormFieldType & {icon?: 'pin' | 'search'}
> = ({component, type = 'text', name, label, icon, ...props}) => {
	return (
		<div className={styles.field().mod(component).mod(type).mod({icon})}>
			{type !== 'checkbox' && (
				<Label htmlFor={props.id || name} label={label} />
			)}
			<div className={styles.field('input').mod(type)}>
				{switchComponent(component, type, name, label, props)}
				{icon && (
					<span className={styles.field('input-icon')}>
						<Icon icon={icon} />
					</span>
				)}
			</div>
			{type !== 'file' && <Errors name={name} />}
		</div>
	)
}

const AgendaFilterFieldLocation: React.FC = () => {
	const t = useTranslation('agenda').filters.location
	const name = 'location'
	const {control, setValue, watch} = useFormContext()
	const [query, setQuery] = useState('')
	const parsePlaces = (items: Array<Prediction>) =>
		items.map((item) => {
			const option: OptionType = {
				amount: null,
				title: item.structured_formatting.main_text,
				path: item.reference
			}
			return option
		})

	const places = parsePlaces(getPlaces(query))
	const selectedPlace = watch(name)
	return (
		<div className={styles.field.mod({icon: true})}>
			<Label htmlFor={name} label={t.label} />
			<Controller
				name={name}
				control={control}
				render={() => (
					<Dropdown
						onChange={(val) => {
							setValue(name, places.find((place) => place.path === val) || null)
						}}
						selected={selectedPlace || null}
						onInputChange={(e) => setQuery(e)}
						items={places}
						enableFilter
						iconComponent={<Icon icon="pin" />}
						mod="border"
						allowClearSelection
					/>
				)}
			/>
		</div>
	)
}

const AgendaFilterTop: React.FC = () => {
	const {setValue, control} = useFormContext()
	const t = useTranslation('agenda').filters
	const radiusName = 'radius'
	const radiusOptions = [
		{amount: null, path: '1', title: '1 km'},
		{amount: null, path: '5', title: '5 km'},
		{amount: null, path: '15', title: '15 km'},
		{amount: null, path: '25', title: '25 km'},
		{amount: null, path: '40', title: '40 km'},
		{amount: null, path: '80', title: '80 km'},
		{amount: null, path: '120', title: '120 km'},
		{amount: null, path: '160', title: '160 km'},
		{amount: null, path: '200', title: '200 km'}
	]

	return (
		<div className={styles.top()}>
			<div className={styles.top('location')}>
				<div className={styles.top('location-city')}>
					<AgendaFilterFieldLocation />
				</div>
				<div className={styles.top('location-radius')}>
					<div className={styles.field.mod({icon: true})}>
						<Label htmlFor={radiusName} label={t.radius} />
						<Controller
							name={radiusName}
							control={control}
							render={({value}) => (
								<Dropdown
									onChange={(val) => {
										setValue(radiusName, val)
									}}
									selected={
										radiusOptions.find((val) => val.path === value) || null
									}
									items={radiusOptions}
									mod="border"
								/>
							)}
						/>
					</div>
				</div>
			</div>
			<div className={styles.top('search')}>
				<AgendaFilterField
					amount={null}
					name="keyword"
					label={t.keyword.label}
					placeholder={t.keyword.placeholder}
					icon="search"
				/>
			</div>
		</div>
	)
}

const AgendaFilterBottom: React.FC<{
	isLoading: boolean
	submitForm: () => void
}> = ({isLoading, submitForm}) => {
	const {control, setValue, watch, reset} = useFormContext()
	const t = useTranslation('agenda').filters
	const dateTypeOptions = [
		{path: 'on', title: t.on},
		{path: 'between', title: t.between}
	] as Array<OptionType>
	const dateTypeName = 'date_type'
	const valueDateType = watch(dateTypeName, dateTypeOptions[0].path)

	const selectedItem = dateTypeOptions?.find(
		(option) => option.path === valueDateType
	)
	return (
		<div className={styles.bottom()}>
			<p className={styles.bottom('label')}>{t.periode}</p>
			<div className={styles.bottom('row')}>
				<p className={styles.bottom('description')}>{t.periode_detail}</p>
				<div className={styles.bottom('row-select')}>
					<Controller
						name={dateTypeName}
						control={control}
						defaultValue={dateTypeOptions[0]?.path}
						render={() => (
							<Dropdown
								onChange={(val) => {
									setValue(dateTypeName, val)
								}}
								selected={selectedItem}
								items={dateTypeOptions}
								mod={['border']}
							/>
						)}
					/>
				</div>
				<div className={styles.bottom('dates')}>
					<DatePicker name="date_start" placeholder="vb. 01/01" type="date" />
					{valueDateType === 'between' && (
						<p className={styles.bottom('dates-divider')}>{t.and}</p>
					)}
					{valueDateType === 'between' && (
						<DatePicker name="date_end" placeholder="vb. 18/01" type="date" />
					)}
				</div>
			</div>
			<div className={styles.bottom('buttons')}>
				<div className={styles.bottom('buttons-submit')}>
					<Theme.Button loading={isLoading} as="button">
						{t.submit}
					</Theme.Button>
				</div>
				<div className={styles.bottom('buttons-reset')}>
					<Theme.Link
						onClick={() => {
							reset()
							submitForm()
						}}
					>
						{t.reset}
					</Theme.Link>
				</div>
			</div>
		</div>
	)
}
